import { useState, useEffect, useMemo } from 'react';
import { txApi, triggerDownload } from '../txApi.js';
import { C, btn, label, th, td, Err, Spinner, Badge, useConfirm } from './_shared.jsx';
import { DuplicateDialog, GroupDuplicateDialog } from './_versioning.jsx';
import { SERIES } from '../../../shared/styles/tokens.js';
import { useWorkspace } from '../../../shared/workspace/WorkspaceContext.jsx';
import { usablePlatforms } from '../platformAccess.js';
import { UtmHistory } from './UtmGrid.jsx';
import UtmDuplicateSummary from './UtmDuplicateSummary.jsx';

// ── Campaign detail modal ─────────────────────────────────────────────────────
//
// A campaign built on an archived taxonomy version is a historical record: it renders
// exactly as it did, and the only way forward is Duplicate, which migrates it onto the
// current taxonomy as a draft. Editing it in place would rewrite history against rules
// that no longer apply.
function DetailModal({ campaign, onClose, onDuplicated }) {
  const [migrating, setMigrating] = useState(false);
  if (!campaign) return null;
  const fv = typeof campaign.field_values === 'string'
    ? JSON.parse(campaign.field_values) : (campaign.field_values || {});
  const fieldMap = Object.fromEntries((campaign.fields || []).map(f => [String(f.id), f.name]));

  return (
    <div className="o-scrim" style={{ zIndex: 1000 }}>
      <div className="o-modal" style={{ maxWidth: 640, maxHeight: '80vh' }}>
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{campaign.campaign_name}</div>
          <button style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 22, lineHeight: 1 }} onClick={onClose}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Client</div><strong style={{ color: C.text }}>{campaign.client_name}</strong></div>
            <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Platform</div><strong style={{ color: C.text }}>{campaign.platform_name}</strong></div>
            <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Created By</div><span style={{ color: C.dim }}>{campaign.created_by_username}</span></div>
            <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Created At</div><span style={{ color: C.dim }}>{new Date(campaign.created_at).toLocaleString()}</span></div>
            {campaign.taxonomy_version_num != null && (
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Taxonomy version</div>
                <Badge color={campaign.taxonomy_version_status === 'active' ? C.success : C.muted}>
                  v{campaign.taxonomy_version_num}
                  {campaign.taxonomy_version_status === 'active' ? ' · current' : ' · archived'}
                </Badge>
              </div>
            )}
            {campaign.is_draft && (
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>State</div>
                <Badge color={C.warn}>Draft — required fields outstanding</Badge>
              </div>
            )}
          </div>

          {campaign.taxonomy_version_status === 'archived' && (
            <div style={{
              marginBottom: 20, fontSize: 12, color: C.warn, background: C.brassWash,
              border: `1px solid ${C.brass}33`, borderRadius: 8, padding: '10px 12px',
            }}>
              <b>Built on an archived taxonomy version.</b> This campaign is a historical
              record and cannot be edited. Duplicate it to bring its values onto the current
              taxonomy — you will see exactly what carries over first.
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <div style={{ ...label(), marginBottom: 8 }}>Taxonomy String</div>
            <div style={{ background: C.s2, borderRadius: 8, padding: '10px 14px', fontFamily: C.mono, fontSize: 13, color: C.code, wordBreak: 'break-all' }}>
              {campaign.taxonomy_string}
            </div>
          </div>
          <div>
            <div style={{ ...label(), marginBottom: 8 }}>Field Values</div>
            {Object.entries(fv).map(([fid, val]) => (
              <div key={fid} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.muted, fontSize: 12, width: 200, flexShrink: 0 }}>{fieldMap[fid] || fid}</span>
                <span style={{ fontSize: 13, color: C.text }}>{val || <span style={{ color: C.muted }}>—</span>}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{
          padding: '12px 24px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button className="o-btn o-btn-sm" onClick={() => setMigrating(true)}>
            Duplicate onto current taxonomy
          </button>
        </div>
      </div>

      {migrating && (
        <DuplicateDialog
          campaignId={campaign.id}
          campaignName={campaign.campaign_name}
          onCancel={() => setMigrating(false)}
          onDone={(r) => { setMigrating(false); onDuplicated?.(r); }}
        />
      )}
    </div>
  );
}

// ── Version History Modal ─────────────────────────────────────────────────────
function VersionHistoryModal({ group, onClose, onViewVersion, utmReadOnly }) {
  const [versions,   setVersions]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [restoring,  setRestoring]  = useState(null);
  const [msg,        setMsg]        = useState(null);
  const [confirmUI, ask] = useConfirm();

  const loadVersions = () =>
    txApi.getGroupVersions(group.id)
      .then(d => setVersions(d.versions || []))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { loadVersions(); }, [group.id]);

  const restore = async (v) => {
    const okay = await ask({
      title: `Restore v${v.version_num} (${v.level})?`,
      body: 'The current version is saved as a new snapshot first, so nothing is lost.',
      confirmLabel: 'Restore this version',
    });
    if (!okay) return;
    setRestoring(v.id);
    setMsg(null);
    try {
      await txApi.restoreGroupVersion(group.id, v.id);
      setMsg({ ok: true, text: `Version v${v.version_num} (${v.level}) restored successfully.` });
      setLoading(true);
      loadVersions();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setRestoring(null);
    }
  };

  // Categorical accents come from the shared series palette — muted by design so
  // they never compete with brass.
  const levelColor = (level) => {
    const idx = level ? level.charCodeAt(0) % SERIES.length : 0;
    return SERIES[idx];
  };

  return (
    <div className="o-scrim" style={{ zIndex: 1000 }}>
      <div className="o-modal" style={{ maxWidth: 760, maxHeight: '82vh' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Version History</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{group.name}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 24, lineHeight: 1 }} onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {msg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
              background: msg.ok ? `${C.success}18` : `${C.danger}18`,
              border: `1px solid ${msg.ok ? C.success : C.danger}40`,
              color: msg.ok ? C.success : C.danger }}>
              {msg.text}
            </div>
          )}
          <section className="o-panel" style={{ padding: 'var(--s4)', marginBottom: 'var(--s3)' }}>
            <UtmHistory groupId={group.id} readOnly={utmReadOnly || !!group.deleted_at} />
          </section>
          {loading ? <Spinner /> : (
            versions.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>
                No previous versions yet.<br />Version snapshots are created automatically each time you re-save a campaign level.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Version</th>
                    <th style={th}>Level</th>
                    <th style={th}>Saved By</th>
                    <th style={th}>Date & Time</th>
                    <th style={th}>Rows</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.id}>
                      <td style={td}>
                        <span style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.brass }}>
                          v{v.version_num}
                        </span>
                      </td>
                      <td style={td}>
                        <span className="o-badge" style={{ background: `${levelColor(v.level)}26`, color: C.dim }}>
                          {v.level}
                        </span>
                      </td>
                      <td style={td}>{v.saved_by_name}</td>
                      <td style={td}>{new Date(v.saved_at).toLocaleString()}</td>
                      <td style={td}>{v.row_count}</td>
                      <td style={td}>
                        <span style={{ display: 'flex', gap: 6 }}>
                          <button style={btn('ghost')} onClick={() => onViewVersion(group, v)}>View</button>
                          <button style={btn('ghost')} onClick={() => restore(v)} disabled={restoring === v.id}>
                            {restoring === v.id ? '…' : 'Restore'}
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
      {confirmUI}
    </div>
  );
}

// ── Main campaigns page ───────────────────────────────────────────────────────
export default function CampaignsPage({ me, onCreateCampaign, onViewCampaign, onDuplicateCampaign, onViewVersion }) {
  // Campaigns are always read in the active workspace. There is no "all clients" view: the
  // client filter this replaces let a user list — and open — campaigns outside the workspace
  // the rest of the module was scoped to.
  const { clientId: filterClient, client: wsClient } = useWorkspace();

  const [campaigns,      setCampaigns]      = useState([]);
  const [platforms,      setPlatforms]      = useState([]);
  // The workspace client carries no platform assignment (/portal/clients is the access list),
  // so the taxonomy clients are read here purely to know which platforms this one is on.
  const [clients,        setClients]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [err,            setErr]            = useState(null);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [detail,         setDetail]         = useState(null);
  const [exporting,      setExporting]      = useState(null);
  const [historyGroup,   setHistoryGroup]   = useState(null);
  const [exportModal,    setExportModal]    = useState(null); // { group, platforms:[{id,name}], selected:Set, exporting:bool }
  const [showDeleted,    setShowDeleted]    = useState(false);
  const [restoring,      setRestoring]      = useState(null);
  const [emptyDuplicate, setEmptyDuplicate] = useState(null); // group whose rows cannot carry over
  const [migrateGroup,   setMigrateGroup]   = useState(null); // group awaiting its field-mapping confirmation
  const [confirmUI, ask] = useConfirm();

  const load = async (cid, pid, deleted = showDeleted) => {
    // The endpoint reads a blank client_id as "every client", so an unscoped call would list
    // campaigns from clients this workspace is not.
    if (!cid) { setCampaigns([]); setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const fn = deleted ? txApi.listDeletedCampaignGroups : txApi.listCampaignGroups;
      const d  = await fn({ client_id: cid || '', platform_id: pid || '' });
      setCampaigns(d.groups || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    txApi.listPlatforms().then(p => setPlatforms(p.platforms)).catch(() => {});
    txApi.listClients().then(c => setClients(c.clients || [])).catch(() => {});
  }, []);

  // Filter by the platforms this workspace's client actually works on — assigned to it AND
  // globally active — not the whole catalogue. A platform that is off is off everywhere it is
  // offered, this control included; an old campaign still sitting on a disabled platform keeps
  // its row and its chip in the table, it just is not something to filter down to.
  const filterablePlatforms = useMemo(
    () => usablePlatforms(clients.find(c => String(c.id) === String(filterClient)), platforms),
    [clients, filterClient, platforms],
  );

  // A platform that leaves the list cannot stay applied — the Apply button would keep
  // filtering on something the control no longer shows.
  useEffect(() => {
    if (filterPlatform && !filterablePlatforms.some(p => String(p.id) === String(filterPlatform))) {
      setFilterPlatform('');
    }
  }, [filterablePlatforms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-list on workspace change, not only on mount — switching client in the top bar has to
  // replace the table, not leave the previous client's campaigns on screen.
  useEffect(() => {
    load(filterClient, filterPlatform, showDeleted);
  }, [filterClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    try { const d = await txApi.getCampaign(id); setDetail(d.campaign); }
    catch (e) { setErr(e.message); }
  };

  const openView = (group) => {
    onViewCampaign({
      initialGroupId:      group.id,
      initialClientId:     group.client_id,
      initialPlatformId:   group.platform_id,
      initialCampaignName: group.name,
    });
  };

  const duplicateCampaign = (group) => {
    // A duplicate is new work, so it cannot carry rows from a disabled platform — the copy
    // would be uneditable the moment it was created. A campaign can span several channels,
    // so this is only a dead end when every channel it uses is disabled; otherwise the
    // surviving channels duplicate and the dialog accounts for the rest.
    const disabled = new Set((group.disabled_platform_ids || []).map(String));
    const channels = (group.platform_ids || [group.platform_id]).map(String);
    if (channels.every(id => disabled.has(id))) { setEmptyDuplicate(group); return; }
    setMigrateGroup(group);
  };

  /**
   * Carry the group's rows onto the current taxonomy using the mapping the user just saw.
   *
   * Values are stored against field ids, and a new taxonomy version re-issues every field
   * with a fresh id — so copying `field_values` verbatim lands every value on a key the new
   * grid does not render, which is why a duplicate onto a changed taxonomy came through
   * blank even where the column names were identical. Fields are re-keyed source id →
   * target id here; anything the new version does not define is dropped, as the dialog said.
   *
   * Keyed by channel and then level, because the mapping is per channel: the same level name
   * under two channels is two different field sets, and each channel is on its own taxonomy
   * version. The builder shows one channel at a time and takes the slice it needs.
   */
  const runGroupDuplicate = async (group, plan) => {
    const disabled = new Set((group.disabled_platform_ids || []).map(String));
    try {
      const gd = await txApi.getCampaignGroup(group.id);
      const mapping = {};  // platformId → levelName → level plan
      (plan.channels || []).forEach(ch => {
        if (disabled.has(String(ch.platform_id))) return;
        ch.levels.forEach(lv => {
          if (lv.unmapped_level) return;
          (mapping[String(ch.platform_id)] ||= {})[lv.level] = lv;
        });
      });

      const rowsByPlatform = {};
      ((gd.group || {}).campaigns || []).forEach(c => {
        const pid = String(c.platform_id);
        const lv = mapping[pid]?.[c.level || ''];
        if (!lv) return;
        const fv = typeof c.field_values === 'string' ? JSON.parse(c.field_values) : (c.field_values || {});
        const values = {};
        (lv.matched || []).forEach(m => {
          const v = fv[m.source_field_id] ?? (m.source_field_key ? fv[m.source_field_key] : undefined);
          if (v !== undefined && v !== null && v !== '') values[m.target_field_id] = v;
        });
        const byLevel = (rowsByPlatform[pid] ||= {});
        (byLevel[c.level || ''] ||= []).push({ id: crypto.randomUUID(), values });
      });

      setMigrateGroup(null);
      onDuplicateCampaign({
        initialClientId:   group.client_id,
        // Opens on the group's own channel; the others are filled in as the user switches to
        // them, which is also how a multi-channel campaign is saved.
        initialPlatformId: group.platform_id,
        initialGridRows:   rowsByPlatform,
        initialUtmSourceId: group.utm_row_count ? group.id : null,
      });
    } catch (e) { setMigrateGroup(null); setErr(e.message); }
  };

  const deleteGroup = async (group) => {
    const okay = await ask({
      title: `Move "${group.name}" to the deleted campaigns list?`,
      body: 'It leaves the campaigns list but is not destroyed — you can restore it later '
          + 'from the Deleted view.',
      confirmLabel: 'Move to deleted',
      tone: 'danger',
    });
    if (!okay) return;
    try {
      await txApi.deleteCampaignGroup(group.id);
      setCampaigns(prev => prev.filter(g => g.id !== group.id));
    } catch (e) { setErr(e.message); }
  };

  const restoreGroup = async (group) => {
    setRestoring(group.id);
    try {
      await txApi.restoreCampaignGroup(group.id);
      setCampaigns(prev => prev.filter(g => g.id !== group.id));
    } catch (e) { setErr(e.message); }
    finally { setRestoring(null); }
  };

  const toggleDeletedView = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    load(filterClient, filterPlatform, next);
  };

  const exportOne = async (id, name) => {
    setExporting(id);
    try {
      const blob = await txApi.exportCampaign(id);
      triggerDownload(blob, `campaign_${name.replace(/\s+/g, '_')}.xlsx`);
    } catch (e) { setErr(e.message); }
    finally { setExporting(null); }
  };

  const exportAll = async () => {
    setExporting('bulk');
    try {
      const blob = await txApi.exportCampaigns({ client_id: filterClient, platform_id: filterPlatform });
      triggerDownload(blob, 'campaigns_export.xlsx');
    } catch (e) { setErr(e.message); }
    finally { setExporting(null); }
  };

  // campaigns state now holds groups directly from the API
  const grouped = campaigns;

  return (
    <div>
      <div className="o-head-row">
        <div>
          {/* The workspace names itself here now that the client filter is gone — without it
              the count would not say which client it counts. */}
          <p className="o-eyebrow">
            {showDeleted ? 'Deleted campaigns' : 'Campaigns'}{wsClient ? ` · ${wsClient.name}` : ''}
          </p>
          <h1 className="o-title">
            {grouped.length} campaign{grouped.length !== 1 ? 's' : ''}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`o-btn ${showDeleted ? 'o-btn-primary' : 'o-btn-ghost'}`} onClick={toggleDeletedView}>
            {showDeleted ? '← Active campaigns' : 'Deleted'}
          </button>
          {!showDeleted && <>
            <button className="o-btn" onClick={exportAll} disabled={exporting === 'bulk' || campaigns.length === 0}>
              {exporting === 'bulk' ? 'Exporting…' : 'Export all'}
            </button>
            <button className="o-btn o-btn-primary" onClick={onCreateCampaign} disabled={!filterClient}>
              Create campaign
            </button>
          </>}
        </div>
      </div>

      <Err msg={err} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 180 }}>
          <label className="o-field-lbl" htmlFor="cf-platform">Platform</label>
          <select className="o-control" id="cf-platform"
            value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}>
            <option value="">All platforms</option>
            {filterablePlatforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button className="o-btn" onClick={() => load(filterClient, filterPlatform, showDeleted)}>Apply</button>
      </div>

      {loading ? <Spinner /> : (
        <div className="o-panel" style={{ overflowX: 'auto' }}>
          <table className="o-tbl">
            <thead>
              <tr>
                <th>Campaign name</th>
                <th>Client</th>
                <th>Platform</th>
                <th>Levels</th>
                <th>Rows</th>
                <th>Status</th>
                <th>Created by</th>
                <th>{showDeleted ? 'Deleted' : 'Created'}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: C.muted }}>
                  {!filterClient
                    ? 'No workspace selected. Pick a client from the workspace selector in the top bar.'
                    : showDeleted
                      ? 'No deleted campaigns.'
                      : <>No campaigns yet — click <strong>Create campaign</strong> to get started.</>}
                </td></tr>
              )}
              {grouped.map((g, i) => (
                <tr key={g.id || i} style={showDeleted ? { opacity: 0.8 } : undefined}>
                  {/* A draft is a campaign that was typed into before it was named: its rows are
                      saved, it just still carries the name the server gave it. Listed like any
                      other campaign, because a hidden draft is its own kind of lost work. */}
                  <td className="o-strong">
                    {g.name}
                    {g.is_draft && (
                      <span className="o-badge" style={{ marginLeft: 8 }} title="Saved, but not named yet — open it and give it a name">
                        Draft
                      </span>
                    )}
                  </td>
                  <td>{g.client_name}</td>
                  <td style={{ color: C.dim }}>
                    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {/* A disabled platform keeps its chip — the campaign still holds its rows —
                          but drops the grey, so the reason the row is read-only is visible
                          before it is opened. Not the deleted treatment; that means something
                          else already. */}
                      {(g.platform_names?.length ? g.platform_names : [g.platform_name]).map(n => (
                        (g.disabled_platform_names || []).includes(n)
                          ? <span key={n} className="o-badge" title={`${n} is disabled — this campaign is read-only`}>{n}</span>
                          : <span key={n} className="o-badge o-grey">{n}</span>
                      ))}
                    </span>
                  </td>
                  <td style={{ color: C.dim }}>
                    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {g.levels.map(lv => <span key={lv} className="o-badge o-grey">{lv}</span>)}
                    </span>
                  </td>
                  <td className="o-num">{g.row_count ?? '—'}</td>
                  <td>
                    {/* Errors outrank read-only: a broken row is the more urgent fact, and a
                        read-only campaign already says so through its platform chip. */}
                    {(g.row_count ?? 0) === 0 ? (
                      <span style={{ color: C.muted }}>—</span>
                    ) : (g.error_count ?? 0) > 0 ? (
                      <span className="o-badge o-err" style={{ whiteSpace: 'nowrap' }}>
                        {g.error_count} error{g.error_count !== 1 ? 's' : ''}
                      </span>
                    ) : (g.disabled_platform_names || []).length > 0 ? (
                      <span className="o-badge" style={{ whiteSpace: 'nowrap' }}>Read-only</span>
                    ) : (
                      <span className="o-badge o-ok">Good</span>
                    )}
                  </td>
                  <td>{g.created_by_username}</td>
                  <td style={{ color: C.muted, whiteSpace: 'nowrap' }}>
                    {new Date(showDeleted ? g.deleted_at : g.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    {showDeleted ? (
                      <div className="o-acts">
                        <button className="o-btn o-btn-sm" onClick={() => openView(g)}>View</button>
                        <button className="o-btn o-btn-sm o-btn-primary"
                          onClick={() => restoreGroup(g)}
                          disabled={restoring === g.id}>
                          {restoring === g.id ? '…' : 'Restore'}
                        </button>
                      </div>
                    ) : (
                      <div className="o-acts">
                        <button className="o-btn o-btn-sm" onClick={() => openView(g)}>View</button>
                        <button className="o-btn o-btn-sm" onClick={() => duplicateCampaign(g)}>Duplicate</button>
                        <button className="o-btn o-btn-sm" onClick={() => {
                          const names = g.platform_names || [g.platform_name];
                          const ids   = g.platform_ids   || [String(g.platform_id)];
                          const plats = names.map((n, idx) => ({ id: ids[idx] || String(g.platform_id), name: n }));
                          setExportModal({ group: g, platforms: plats, selected: new Set(plats.map(p => p.id)), exporting: false });
                        }}>xlsx</button>
                        <button className="o-btn o-btn-sm" onClick={() => setHistoryGroup(g)}>History</button>
                        <button className="o-btn o-btn-sm" onClick={() => deleteGroup(g)}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <DetailModal
          campaign={detail}
          onClose={() => setDetail(null)}
          // The duplicate lands as a draft on the current version; close onto the refreshed
          // list rather than leaving a stale detail view open behind it.
          onDuplicated={() => { setDetail(null); load(filterClient, filterPlatform); }}
        />
      )}

      {historyGroup && (
        <VersionHistoryModal
          group={historyGroup}
          utmReadOnly={!me?.role?.can_use_builder}
          onClose={() => setHistoryGroup(null)}
          onViewVersion={(g, v) => onViewVersion?.(g, v)}
        />
      )}

      {/* Which columns survive the copy is the whole question when the taxonomy has moved on,
          so it is answered before the builder opens rather than left to be inferred from a
          half-filled grid. */}
      {migrateGroup && (
        <GroupDuplicateDialog
          group={migrateGroup}
          onCancel={() => setMigrateGroup(null)}
          onConfirm={plan => runGroupDuplicate(migrateGroup, plan)}
        />
      )}

      {/* Duplicating a campaign whose platform is disabled would carry nothing over. Say so
          rather than opening a blank builder and letting the user work out why it is empty. */}
      {emptyDuplicate && (
        <div className="o-scrim" onClick={() => setEmptyDuplicate(null)}>
          <div className="o-modal" onClick={e => e.stopPropagation()}>
            <div className="o-modal-head"><h2>Duplicate {emptyDuplicate.name}?</h2></div>
            <div className="o-modal-body">
              <p style={{ margin: 0, fontSize: 13, color: C.dim }}>
                <b style={{ color: C.text }}>
                  {(emptyDuplicate.disabled_platform_names || []).join(', ') || 'That platform'} is disabled
                </b>, so none of this campaign's rows can be carried over — a copy of them would
                be read-only the moment it was made. The original keeps everything it has.
              </p>
              <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                Duplicating now starts an empty campaign for {emptyDuplicate.client_name}, on a
                channel you pick.
              </p>
            </div>
            <div className="o-modal-foot">
              <button className="o-btn" onClick={() => setEmptyDuplicate(null)}>Cancel</button>
              <button className="o-btn o-btn-primary" onClick={() => {
                const g = emptyDuplicate;
                setEmptyDuplicate(null);
                onDuplicateCampaign({ initialClientId: g.client_id });
              }}>Start an empty campaign</button>
            </div>
          </div>
        </div>
      )}

      {exportModal && (
        <div className="o-scrim" style={{ zIndex: 200 }}
          onClick={() => !exportModal.exporting && setExportModal(null)}>
          <div className="o-modal" onClick={e => e.stopPropagation()}
            style={{ padding: '24px 28px', maxWidth: 360 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Export by Platform</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>
              <em>{exportModal.group.name}</em> — one file per platform
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {exportModal.platforms.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: C.text }}>
                  <input type="checkbox"
                    checked={exportModal.selected.has(p.id)}
                    onChange={e => setExportModal(prev => {
                      const s = new Set(prev.selected);
                      e.target.checked ? s.add(p.id) : s.delete(p.id);
                      return { ...prev, selected: s };
                    })}
                    style={{ accentColor: C.ink, width: 15, height: 15 }}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={btn('ghost', { fontSize: 13 })} onClick={() => setExportModal(null)} disabled={exportModal.exporting}>Cancel</button>
              <button style={btn('primary', { fontSize: 13, padding: '7px 20px' })}
                disabled={exportModal.exporting || exportModal.selected.size === 0}
                onClick={async () => {
                  setExportModal(prev => ({ ...prev, exporting: true }));
                  for (const p of exportModal.platforms) {
                    if (!exportModal.selected.has(p.id)) continue;
                    try {
                      const blob = await txApi.exportCampaignGroup(exportModal.group.id, p.id, p.name);
                      const safeName = exportModal.group.name.replace(/\s+/g, '_').replace(/[^\w_-]/g, '').slice(0, 40);
                      const safePlat = p.name.replace(/\s+/g, '_').replace(/[^\w_-]/g, '').slice(0, 20);
                      triggerDownload(blob, `${safeName}_${safePlat}.xlsx`);
                      await new Promise(r => setTimeout(r, 300));
                    } catch (e) { setErr(e.message); }
                  }
                  setExportModal(null);
                }}>
                {exportModal.exporting ? 'Exporting…' : `Export ${exportModal.selected.size} file${exportModal.selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmUI}
    </div>
  );
}
